/* @license
 * Copyright 2019 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { DoubleSide, LinearFilter, Mesh, MeshStandardMaterial, Object3D, PlaneGeometry, SphereGeometry, SRGBColorSpace, } from "three";
import { createGIFTexture } from "../utilities/gif-texture.js";
import { loadImageTexture } from "../utilities/image-loader.js";
export const ImageProjection = {
    Flat: "flat",
    Equirectangular360: "360-equirectangular",
};
export const ImageAlphaMode = {
    Opaque: "opaque",
    Blend: "blend",
    Mask: "mask",
};
/**
 * Image object that can display 2D images in a 3D scene.
 * Supports flat and 360-degree equirectangular projections.
 */
export class ImageObject extends Object3D {
    constructor() {
        super();
        this._src = null;
        this._projection = ImageProjection.Flat;
        this._alphaMode = ImageAlphaMode.Opaque;
        this._alphaCutoff = 0.5;
        this._texture = null;
        this._animatedTexture = null;
        const geometry = new PlaneGeometry();
        const material = new MeshStandardMaterial();
        material.side = DoubleSide;
        material.transparent = this._alphaMode === ImageAlphaMode.Blend;
        material.alphaTest =
            this._alphaMode === ImageAlphaMode.Mask ? this._alphaCutoff : 0;
        // Configure for unlit appearance (like MeshBasicMaterial)
        material.emissiveIntensity = 1.0;
        material.roughness = 1.0;
        material.metalness = 0.0;
        this._mesh = new Mesh(geometry, material);
        this._mesh.name = "ImageMesh";
        this.add(this._mesh);
        this._mesh.visible = false;
    }
    get src() {
        return this._src;
    }
    set src(src) {
        if (src) {
            this.load(src).catch(console.error);
        }
    }
    async loadTexture(src) {
        if (this.isGifSource(src)) {
            const gifTexture = await createGIFTexture(src);
            this._animatedTexture = gifTexture;
            return gifTexture;
        }
        this._animatedTexture = null;
        return loadImageTexture(src);
    }
    isGifSource(src) {
        try {
            const base = typeof window !== "undefined" ? window.location.href : undefined;
            const url = new URL(src, base);
            return url.pathname.toLowerCase().endsWith(".gif");
        }
        catch (_a) {
            return src.toLowerCase().includes(".gif");
        }
    }
    get alphaMode() {
        return this._alphaMode;
    }
    set alphaMode(v) {
        this._alphaMode = v;
        const material = this._mesh.material;
        material.transparent = v === ImageAlphaMode.Blend;
        material.alphaTest = v === ImageAlphaMode.Mask ? this._alphaCutoff : 0;
        material.needsUpdate = true;
    }
    get alphaCutoff() {
        return this._alphaCutoff;
    }
    set alphaCutoff(v) {
        this._alphaCutoff = v;
        if (this._alphaMode === ImageAlphaMode.Mask) {
            const material = this._mesh.material;
            material.alphaTest = v;
            material.needsUpdate = true;
        }
    }
    get projection() {
        return this._projection;
    }
    set projection(projection) {
        const material = new MeshStandardMaterial();
        // Configure for unlit appearance (like MeshBasicMaterial)
        material.emissiveIntensity = 1.0;
        material.roughness = 1.0;
        material.metalness = 0.0;
        let geometry;
        if (projection === ImageProjection.Equirectangular360) {
            geometry = new SphereGeometry(1, 64, 32);
            // Invert the geometry on the x-axis so that all of the faces point inward
            geometry.scale(-1, 1, 1);
        }
        else {
            geometry = new PlaneGeometry();
            material.side = DoubleSide;
        }
        material.map = this._texture;
        // Set emissive to match map for unlit appearance
        if (this._texture) {
            material.emissiveMap = this._texture;
        }
        material.transparent = this._alphaMode === ImageAlphaMode.Blend;
        material.alphaTest =
            this._alphaMode === ImageAlphaMode.Mask ? this._alphaCutoff : 0;
        this._projection = projection;
        const nextMesh = new Mesh(geometry, material);
        nextMesh.name = "ImageMesh";
        nextMesh.visible = this._mesh.visible;
        const meshIndex = this.children.indexOf(this._mesh);
        if (meshIndex === -1) {
            this.add(nextMesh);
        }
        else {
            this.children.splice(meshIndex, 1, nextMesh);
            nextMesh.parent = this;
        }
        this._mesh = nextMesh;
        this.onResize();
    }
    async load(src) {
        this._src = src;
        this._mesh.visible = false;
        const material = this._mesh.material;
        if (material.map) {
            material.map.dispose();
        }
        if (this._animatedTexture) {
            this._animatedTexture.dispose();
            this._animatedTexture = null;
        }
        if (material.emissiveMap && material.emissiveMap !== material.map) {
            material.emissiveMap.dispose();
        }
        const texture = await this.loadTexture(src);
        // TODO: resize to maintain aspect ratio but still allow scaling.
        texture.colorSpace = SRGBColorSpace;
        texture.minFilter = LinearFilter;
        this._texture = texture;
        // Detect if image has transparency and auto-enable blend mode if needed
        const hasTransparency = await this.detectTransparency(texture);
        if (hasTransparency && this._alphaMode === ImageAlphaMode.Opaque) {
            // Automatically switch to blend mode for images with transparency
            this._alphaMode = ImageAlphaMode.Blend;
        }
        this.onResize();
        material.transparent = this._alphaMode === ImageAlphaMode.Blend;
        material.alphaTest =
            this._alphaMode === ImageAlphaMode.Mask ? this._alphaCutoff : 0;
        material.map = this._texture;
        // Set emissive to match map for unlit appearance (like MeshBasicMaterial)
        material.emissiveMap = this._texture;
        material.needsUpdate = true;
        this._mesh.visible = true;
        return this;
    }
    /**
     * Detects if an image texture has transparency by checking the alpha channel.
     * @param texture The texture to check
     * @returns Promise that resolves to true if the image has transparency
     */
    async detectTransparency(texture) {
        const image = texture.image;
        if (!image) {
            return false;
        }
        // If image is a canvas, check it directly
        if (image instanceof HTMLCanvasElement) {
            return this.checkCanvasAlpha(image);
        }
        // For HTMLImageElement, ensure it's loaded before checking
        if (image instanceof HTMLImageElement) {
            if (image.complete && image.naturalWidth > 0) {
                return this.checkImageAlpha(image);
            }
            else {
                // Wait for image to load
                return new Promise((resolve) => {
                    const checkComplete = () => {
                        if (image.complete && image.naturalWidth > 0) {
                            resolve(this.checkImageAlpha(image));
                        }
                        else {
                            // Fallback: wait a bit and try again
                            setTimeout(() => {
                                if (image.complete && image.naturalWidth > 0) {
                                    resolve(this.checkImageAlpha(image));
                                }
                                else {
                                    resolve(false);
                                }
                            }, 100);
                        }
                    };
                    if (image.complete) {
                        checkComplete();
                    }
                    else {
                        image.onload = checkComplete;
                        image.onerror = () => resolve(false);
                        // Timeout fallback
                        setTimeout(() => resolve(false), 5000);
                    }
                });
            }
        }
        // For other image types (video, etc.), assume no transparency
        return false;
    }
    /**
     * Checks if an HTMLImageElement has transparency by examining pixel data.
     * Uses sampling for performance on large images.
     */
    checkImageAlpha(image) {
        try {
            const canvas = document.createElement("canvas");
            // Limit canvas size for performance (max 512x512 for sampling)
            const maxSampleSize = 512;
            const scale = Math.min(1, maxSampleSize / Math.max(image.width, image.height));
            canvas.width = Math.floor(image.width * scale);
            canvas.height = Math.floor(image.height * scale);
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                return false;
            }
            ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            // Check if any pixel has alpha < 255
            // Sample every 4th pixel for better performance on large images
            const sampleStep = Math.max(1, Math.floor(data.length / 10000));
            for (let i = 3; i < data.length; i += 4 * sampleStep) {
                if (data[i] < 255) {
                    return true;
                }
            }
            return false;
        }
        catch (error) {
            // If checking fails, assume no transparency to be safe
            console.warn("Failed to check image transparency:", error);
            return false;
        }
    }
    /**
     * Checks if an HTMLCanvasElement has transparency.
     * Uses sampling for performance on large canvases.
     */
    checkCanvasAlpha(canvas) {
        try {
            const ctx = canvas.getContext("2d", { willReadFrequently: true });
            if (!ctx) {
                return false;
            }
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            // Check if any pixel has alpha < 255
            // Sample every 4th pixel for better performance on large canvases
            const sampleStep = Math.max(1, Math.floor(data.length / 10000));
            for (let i = 3; i < data.length; i += 4 * sampleStep) {
                if (data[i] < 255) {
                    return true;
                }
            }
            return false;
        }
        catch (error) {
            // If checking fails, assume no transparency to be safe
            console.warn("Failed to check canvas transparency:", error);
            return false;
        }
    }
    onResize() {
        if (this._texture && this.projection === ImageProjection.Flat) {
            const ratio = (this._texture.image.height || 1.0) /
                (this._texture.image.width || 1.0);
            const width = Math.min(1.0, 1.0 / ratio);
            const height = Math.min(1.0, ratio);
            this._mesh.scale.set(width, height, 1);
        }
    }
    copy(source, recursive = true) {
        if (recursive) {
            this.remove(this._mesh);
        }
        super.copy(source, recursive);
        if (recursive) {
            const _meshIndex = source.children.indexOf(source._mesh);
            if (_meshIndex !== -1) {
                this._mesh = this.children[_meshIndex];
            }
        }
        this.projection = source.projection;
        if (source.src) {
            this.src = source.src;
        }
        return this;
    }
    dispose() {
        const material = this._mesh.material;
        if (material.map) {
            material.map.dispose();
        }
        // emissiveMap is the same as map, so don't dispose twice
        if (material.emissiveMap && material.emissiveMap !== material.map) {
            material.emissiveMap.dispose();
        }
        material.dispose();
        this._mesh.geometry.dispose();
        this.remove(this._mesh);
        if (this._animatedTexture) {
            this._animatedTexture.dispose();
            this._animatedTexture = null;
        }
    }
    update() {
        var _a;
        (_a = this._animatedTexture) === null || _a === void 0 ? void 0 : _a.update();
    }
    /**
     * Returns true if the image needs continuous rendering (e.g., animated GIF)
     */
    needsContinuousRendering() {
        return this._animatedTexture !== null;
    }
}
//# sourceMappingURL=ImageObject.js.map